import { useEffect, useState, useCallback, useMemo } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { useNavigate } from "react-router-dom";
import { createApiEndpoint } from '../index.js';
import Upload from '../assets/uploads/Upload.js';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { outlinedActionButtonSx } from './windowChrome/index.js';

const CarbonDioxideUpload = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(null);
    const [excelData, setExcelData] = useState(null);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: isLight ? '#000000' : '#f4cc8e',
                    fontSize: "18px",
                    borderColor: isLight ? '#666666' : 'rgb(244, 204, 142)',
                    ...(isLight && {
                        '&:not(.MuiInputLabel-shrink)': {
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: '14px',
                        },
                    }),
                },
                shrink: {
                    top: isLight ? '8px' : undefined,
                },
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                    background: isLight ? '#e0e0e0' : '#2d2d2d',
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)',
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                sizeMedium: {
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    },
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                    "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                },
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                    ".MuiSvgIcon-root": {
                        color: isLight ? '#333333' : '#f4cc8e',
                    },
                },
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                },
            }
        },
    },
    });
    }, [forceUpdate]);

    const renderModal = () => {
        return (   
            <MuiMaterials.Modal
                key={`co2-upload-modal-${global.gw.theme}-${forceUpdate}`}
                open={isModalOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{
                    backgroundColor: getThemeColor("rgba(255, 255, 255, 0.45)", "rgba(0, 0, 0, 0.45)"),
                }}
            >
                <MuiMaterials.Box
                    sx={{
                        color: getThemeColor('#333333', '#f4cc8e'),
                        display: "flex",
                        flexDirection: "row",
                        height: "100%",
                        width: "100%",
                        justifyContent: "center",
                        alignContent: "start",
                        flexWrap: "wrap",
                        alignItems: "center",
                        fontWeight: "900",
                        overflowY: "hidden",
                        padding: "50px 0px",
                        background: getThemeColor('#ffffff', '#2d2d2d'),
                    }}
                >
                    <MuiMaterials.Button
                        sx={{
                            color: getThemeColor('#333333', '#f4cc8e'),
                            margin: "auto",
                            maxWidth: "40px",
                            maxHeight: "40px",
                            minWidth: "40px",
                            minHeight: "40px",
                            position: "absolute",
                            right: "0",
                            top: "0",
                            '&:hover': {
                                backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                            },
                        }}
                        onClick={e => {
                            setIsModalOpen(false);
                        }}
                    >
                        <CloseIcon />
                    </MuiMaterials.Button>

                    <MuiMaterials.Box>
                        <MuiMaterials.InputLabel
                            sx={{
                                fontSize: "18px",
                                background: getThemeColor('#ffffff', '#2d2d2d'),
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "5px 0px",
                                textAlign: "center",
                                padding: "9px 14px",
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                    },
                                },
                            }}
                        >
                            Upload Weather Station File
                        </MuiMaterials.InputLabel>
                        <MuiMaterials.Box
                            noValidate
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                flexWrap: "wrap",
                            }}
                        >
                            <Upload
                                accept={".xlsx, .xls"}
                                context={"Select File"}
                                isExcel={true}
                                excelData={excelData}
                                setExcelData={setExcelData}
                            />
                            <MuiMaterials.Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginTop: "5px",
                                }}
                            >
                                <MuiMaterials.Button
                                    key={`co2-upload-cancel-${global.gw.theme}-${forceUpdate}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: "114px", ...outlinedActionButtonSx() }}
                                    onClick={async () => {
                                        setIsModalOpen(false);
                                    }}
                                >
                                    Cancel
                                </MuiMaterials.Button>
                                <MuiMaterials.Button
                                    key={`co2-upload-upload-${global.gw.theme}-${forceUpdate}`}
                                    type="submit"
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: "114px", ...outlinedActionButtonSx() }}
                                    onClick={async () => {
                                        if (excelData && excelData.length > 0) {
                                            await createApiEndpoint().fetchUploadWeatherStation("direct", excelData);
                                            await global.setAllSitesWeatherStationReadings();
                                        }
                                        setIsModalOpen(false);
                                        navigate("/sites");
                                    }}
                                >
                                    Upload
                                </MuiMaterials.Button>
                            </MuiMaterials.Box>
                            {excelData && (
                                <pre>{JSON.stringify(excelData, null, 2)}</pre>
                            )}
                        </MuiMaterials.Box>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        );
    };

    return (
        <>
            <MuiMaterials.Button
                key={`co2-upload-button-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    color: getThemeColor('#333333', '#f4cc8e'),
                    margin: "auto",
                    maxWidth: "40px",
                    maxHeight: "40px",
                    minWidth: "40px",
                    minHeight: "40px",
                    '&:hover': {
                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                    },
                }}
                onClick={e => {
                    setIsModalOpen(true);
                }}
            >
                <FileUploadIcon className="sei" />
            </MuiMaterials.Button>
            {renderModal()}
        </>
    );
}
export default CarbonDioxideUpload;